import React, { useEffect, useState } from 'react';
import { PurpleNavBar } from '../../dashboard_user/components/PurpleNavBar';
import { COLORS } from '../../../core/constants/colors';
import { Company } from '../../dashboard_user/types';
import { linkedInEnrichmentService } from '../services/linkedin-enrichment.service';
import {
  LinkedInEnrichmentStatus,
  LinkedInConsentSubmission,
} from '../types';
import { api } from '../../../core/utils/apiClient';

const isValidLinkedInUrl = (value: string) => {
  try {
    const parsed = new URL(value);
    const isLinkedInHost =
      parsed.hostname === 'linkedin.com' ||
      parsed.hostname === 'www.linkedin.com' ||
      parsed.hostname.endsWith('.linkedin.com');
    const isValidPath =
      parsed.pathname.startsWith('/company/') ||
      parsed.pathname.startsWith('/school/') ||
      parsed.pathname.startsWith('/in/') ||
      parsed.pathname.startsWith('/showcase/');
    return isLinkedInHost && isValidPath;
  } catch {
    return false;
  }
};

export const LinkedInEnrichmentPage: React.FC = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [consentAck, setConsentAck] = useState(false);

  const [, setSubmission] = useState<LinkedInConsentSubmission | null>(null);
  const [status, setStatus] = useState<LinkedInEnrichmentStatus | null>(null);

  const [loadingCompany, setLoadingCompany] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMyCompany();
  }, []);

  useEffect(() => {
    if (company?.id) {
      loadStatus(company.id);
    } else {
      setStatus(null);
      setSubmission(null);
    }
  }, [company?.id]);

  const loadMyCompany = async () => {
    setLoadingCompany(true);
    try {
      const response = await api.get('/api/v1/companies/my-companies');
      
      if (response.status === 404) {
        setError('No company found. Please contact support.');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        // Handle list response - take first company
        const companiesList = Array.isArray(data) ? data : (data.companies || []);
        const partnerCompany = companiesList.length > 0 ? companiesList[0] : null;
        setCompany(partnerCompany);
      }
    } catch (err) {
      console.error('Failed to load company', err);
      setError('Unable to load your company. Please refresh and try again.');
    } finally {
      setLoadingCompany(false);
    }
  };

  const loadStatus = async (companyId: number) => {
    setIsFetchingStatus(true);
    setError(null);
    try {
      const result = await linkedInEnrichmentService.getStatus(companyId);
      setStatus(result);
      if (result.linkedin_url) {
        setLinkedinUrl(result.linkedin_url);
      }
    } catch (err: any) {
      // No status yet is fine
      setStatus(null);
    } finally {
      setIsFetchingStatus(false);
    }
  };

  const handleSubmit = async () => {
    if (!company?.id) {
      setError('Company not loaded. Please refresh the page.');
      return;
    }

    const targetUrl = linkedinUrl.trim();
    if (!targetUrl) {
      setError('Please provide a LinkedIn URL.');
      return;
    }

    if (!isValidLinkedInUrl(targetUrl)) {
      setError('Enter a valid LinkedIn company URL (e.g., https://www.linkedin.com/company/...)');
      return;
    }

    if (!consentAck) {
      setError('You must acknowledge consent before submitting.');
      return;
    }

    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    try {
      const result = await linkedInEnrichmentService.submitConsent(
        company.id,
        targetUrl,
        true
      );
      setSubmission(result);
      setMessage('LinkedIn enrichment request submitted successfully. Processing will begin shortly.');
      await loadStatus(company.id);
      setConsentAck(false);
    } catch (err: any) {
      setError(err.message || 'Failed to submit LinkedIn enrichment request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = async () => {
    if (!company?.id) return;
    setIsRetrying(true);
    setError(null);
    setMessage(null);
    try {
      await linkedInEnrichmentService.retry(company.id);
      setMessage('Retry requested. Processing has been restarted.');
      await loadStatus(company.id);
    } catch (err: any) {
      setError(err.message || 'Unable to retry this request.');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRefresh = () => {
    if (company?.id) {
      loadStatus(company.id);
      setMessage('Status refreshed.');
    }
  };

  const getStatusColor = (statusValue: string) => {
    switch (statusValue?.toLowerCase()) {
      case 'completed':
        return { bg: '#E8F5E9', text: '#2E7D32' };
      case 'processing':
        return { bg: '#E3F2FD', text: '#1565C0' };
      case 'failed':
        return { bg: '#FFEBEE', text: '#C62828' };
      case 'pending':
        return { bg: '#FFF3E0', text: '#E65100' };
      case 'not_started':
        return { bg: '#F5F5F5', text: '#616161' };
      default:
        return { bg: '#F5F5F5', text: '#616161' };
    }
  };

  if (loadingCompany) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
        <PurpleNavBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading your company...</p>
          </div>
        </div>
      </div>
    );
  }

  const statusColors = status ? getStatusColor(status.status) : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
      <PurpleNavBar />

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#1A1A1A' }}>
            LinkedIn Enrichment
          </h1>
          <p className="text-gray-600">
            Submit a company LinkedIn profile for automated data enrichment
          </p>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">{message}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <div>
                <h2 className="text-xl font-bold mb-1" style={{ color: '#1A1A1A' }}>
                  Submit for Enrichment
                </h2>
                <p className="text-sm text-gray-600">
                  Provide company LinkedIn profile to extract business information
                </p>
              </div>

              <div className="space-y-4">
                {/* Company Display */}
                {company && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-xs text-gray-600 mb-1">Company</p>
                    <p className="text-lg font-bold" style={{ color: '#1A1A1A' }}>
                      {company.name}
                    </p>
                    {company.industry && (
                      <p className="text-sm text-gray-600 mt-1">{company.industry}</p>
                    )}
                  </div>
                )}

                {/* LinkedIn URL */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    LinkedIn Profile URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://www.linkedin.com/company/example"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    className="w-full rounded-lg border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
                    style={{ borderColor: '#E0E0E0' }}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the full LinkedIn company profile URL
                  </p>
                </div>

                {/* Consent */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={consentAck}
                      onChange={(e) => setConsentAck(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                    />
                    <label htmlFor="consent" className="text-sm text-gray-700 cursor-pointer">
                      I acknowledge that this enrichment will collect publicly available information from the provided LinkedIn profile. I confirm I have the right to request this data extraction.
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !consentAck || !linkedinUrl.trim()}
                  className="w-full py-3 px-6 rounded-xl font-semibold text-white transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: COLORS.user.queryButton }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit for Enrichment'}
                </button>
              </div>
            </div>
          </div>

          {/* Status Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold" style={{ color: '#1A1A1A' }}>
                  Enrichment Status
                </h3>
                <button
                  onClick={handleRefresh}
                  disabled={isFetchingStatus}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                  title="Refresh status"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>

              {isFetchingStatus && (
                <div className="text-center py-8">
                  <div
                    className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                    style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                  />
                </div>
              )}

              {!isFetchingStatus && status && (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Current Status</p>
                    {statusColors && (
                      <span
                        className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                        style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                      >
                        {status.status}
                      </span>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">Completion</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{
                            width: `${status.completion_percentage ?? 0}%`,
                            backgroundColor: COLORS.user.primary,
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
                        {status.completion_percentage ?? 0}%
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">Fields Extracted</p>
                    <p className="text-lg font-bold" style={{ color: '#1A1A1A' }}>
                      {status.fields_extracted ?? 0} / {status.total_possible_fields ?? 25}
                    </p>
                  </div>

                  {status.extraction_started_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Started At</p>
                      <p className="text-sm" style={{ color: '#1A1A1A' }}>
                        {new Date(status.extraction_started_at).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {status.extraction_completed_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Completed At</p>
                      <p className="text-sm" style={{ color: '#1A1A1A' }}>
                        {new Date(status.extraction_completed_at).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {status.error_message && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-800">{status.error_message}</p>
                    </div>
                  )}

                  {status.can_retry && (
                    <button
                      onClick={handleRetry}
                      disabled={isRetrying}
                      className="w-full py-2.5 px-4 rounded-lg font-medium text-white transition-all hover:shadow-md disabled:opacity-50"
                      style={{ backgroundColor: COLORS.user.primary }}
                    >
                      {isRetrying ? 'Retrying...' : 'Retry Enrichment'}
                    </button>
                  )}
                </div>
              )}

              {!isFetchingStatus && !status && (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-500">
                    No enrichment data yet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

